import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Modal, ModalProps, Stack} from "@mantine/core";
import {useCallback} from "react";
import {getObjectId, PersonEntity, TrailerEntity, VehicleEntity} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {PersonSelect} from "../../select/entity/PersonSelect/PersonSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";

interface ShiftFilterFormProps {
  defaultValues?: Partial<ShiftFilterFormValues>;
  onSubmit?: (values: ShiftFilterFormValues) => void;
}

export interface ShiftFilterFormValues {
  person?: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer?: TrailerEntity | string;
}

export const DEFAULT_SHIFT_FILTER: ShiftFilterFormValues = {
  person: undefined,
  vehicle: undefined,
  trailer: undefined,
}

export const isDefaultShiftFilter = (filter1: ShiftFilterFormValues): boolean => {
  if (filter1 === DEFAULT_SHIFT_FILTER) {
    return true
  }

  if (getObjectId(filter1.person) !== getObjectId(DEFAULT_SHIFT_FILTER.person)) {
    return false;
  }

  if (getObjectId(filter1.vehicle) !== getObjectId(DEFAULT_SHIFT_FILTER.vehicle)) {
    return false;
  }

  if (getObjectId(filter1.trailer) !== getObjectId(DEFAULT_SHIFT_FILTER.trailer)) {
    return false;
  }

  return true;
}

export const ShiftFilterForm = (props: ShiftFilterFormProps) => {
  const {
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: {
      person: getObjectId(defaultValues?.person),
      vehicle: getObjectId(defaultValues?.vehicle),
      trailer: getObjectId(defaultValues?.trailer),
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        person: values.person,
        vehicle: values.vehicle,
        trailer: values.trailer,
      });
    }
  }, [form, onSubmit]);

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <PersonSelect
          label={t("common.entity.shift.filter.form.field.person", "User")}
          placeholder={t<string>("common.entity.shift.filter.form.field.person", "User")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("person")} />
        <VehicleSelect
          label={t("common.entity.shift.filter.form.field.vehicle", "Truck")}
          placeholder={t<string>("common.entity.shift.filter.form.field.vehicle", "Truck")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("vehicle")} />
        <TrailerSelect
          label={t("common.entity.shift.filter.form.field.trailer", "Trailer")}
          placeholder={t<string>("common.entity.shift.filter.form.field.trailer", "Trailer")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("trailer")} />
      </Stack>
      <Button type="submit" fullWidth mt="xl">
        {t("common.action.submit", {ns: "common"})}
      </Button>
    </form>
  );
};

interface ShiftFilterFormModalProps extends ShiftFilterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ShiftFilterFormModal = (props: ShiftFilterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ShiftFilterForm defaultValues={defaultValues} onSubmit={onSubmit}/>
    </Modal>
  );
};
